import AddressEditor from '@app/components/AddressEditor'
import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import CompCard from '@app/components/CompCard'
import CompsEditor from '@app/components/CompsEditor'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import LotCard from '@app/components/LotCard'
import LotEditorModal from '@app/components/LotEditorModal'
import LotSizeInput from '@app/components/LotSizeInput'
import PillListRenderer from '@app/components/PillListRenderer'
import SignListEditor from '@app/components/SignListEditor'
import { SocialConnectionEditor } from '@app/components/SocialConnection'
import SpaceCard from '@app/components/SpaceCard'
import SpaceEditorModal from '@app/components/SpaceEditorModal'
import StringListEditor from '@app/components/StringListEditor'
import TransactionHistoryModal from '@app/components/TransactionHistoryModal'
import { IComp } from '@app/models/Comp'
import { PropertyType } from '@app/models/enums'
import { ILot } from '@app/models/Lot'
import { Property, IProperty } from '@app/models/Property'
import { ISpace } from '@app/models/Space'
import {
  ITransactionHistory,
  TransactionHistory,
} from '@app/models/TransactionHistory'
import { ICurrentUserState } from '@app/store/modules/account'
import {
  IGlobalCompaniesState,
  IGlobalCompanyContactsState,
  IGlobalConfigurationState,
  IGlobalSocialNetworksState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatCurrency, formatDate } from '@app/utils/formatting'
import { humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Loader } from '@googlemaps/js-api-loader'
import * as qs from 'query-string'
import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Alert, Button, Container, Input } from 'reactstrap'
import {
  actionCreators,
  ATTACHMENTS_IDENTIFIER,
  IPropertyEditState,
  PHOTOS_IDENTIFIER,
} from '../modules/propertyEdit'
import './PropertyEdit.scss'
import PropertyTypeSelector from '@app/components/PropertyTypeSelector/components/PropertyTypeSelector'
import PageSection from '@app/components/PageSection'

interface IConnectionViewRouterProps {
  id: string
}

interface PropertyEditProps {
  quickAdd?: boolean
  propertyId?: string
  siteId?: string
  className?: string
}

interface IComponentState {
  lotModalIsOpen: boolean
  showConfirmDelete: any
  showConfirmEdit: any
  spaceModalIsOpen: boolean
  transaction: ITransactionHistory
  transactionModalIsOpen: boolean
}

type IComponentProps = IPropertyEditState &
  ICurrentUserState &
  IGlobalCompanyContactsState &
  IGlobalCompaniesState &
  IGlobalConfigurationState &
  IGlobalSocialNetworksState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators &
  PropertyEditProps

export default class PropertyEdit extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)

    this.state = {
      lotModalIsOpen: false,
      showConfirmDelete: {},
      showConfirmEdit: {},
      spaceModalIsOpen: false,
      transaction: new TransactionHistory(),
      transactionModalIsOpen: false,
    }
  }

  public componentDidMount() {
    if (this.props.propertyId) {
      this.props.loadEditProperty(this.props.propertyId)
    } else {
      this.props.loadAddProperty(this.props.siteId, this.props.quickAdd)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.property) {
      return (
        <div className='my-3'>
          <div className='mb-3 text-center'>
            Retrieving Property Information
          </div>
          {this.props.error && <Alert color='danger'>{this.props.error}</Alert>}
        </div>
      )
    }

    return (
      <Container className={`my-3 ${this.props.className}`}>
        {!(this.props.quickAdd && this.props.propertyId) &&
          this._renderBasicInfo(this.props.property)}
        {!this.props.quickAdd && this._renderCoordinates()}
        {this._renderOwners()}
        {!this.props.quickAdd && this._renderPropertyInformation()}
        {!this.props.quickAdd && this._renderHighlights()}

        {!this.props.quickAdd &&
          (!this.props.property.propertyTypes.contains(PropertyType.Land) ||
            !this.props.property.propertyTypes.contains(
              PropertyType.AgriculturalLand,
            ) ||
            !this.props.property.propertyTypes.contains(
              PropertyType.RecreationalLand,
            )) && (
            <div>
              <SpaceEditorModal
                isOpen={this.state.spaceModalIsOpen}
                space={undefined}
                onConfirmClick={(e: ISpace) => {
                  this.setState({ spaceModalIsOpen: false })
                  this.props.addSpace(e)
                }}
                onCancelClick={() => this.setState({ spaceModalIsOpen: false })}
              />
              {this._renderSpaces()}
            </div>
          )}

        {!this.props.quickAdd &&
          (this.props.property.propertyTypes.contains(PropertyType.Land) ||
            this.props.property.propertyTypes.contains(
              PropertyType.AgriculturalLand,
            ) ||
            this.props.property.propertyTypes.contains(
              PropertyType.RecreationalLand,
            )) && (
            <div>
              <LotEditorModal
                isOpen={this.state.lotModalIsOpen}
                lot={undefined}
                onConfirmClick={(e: ILot) => {
                  this.setState({ lotModalIsOpen: false })
                  this.props.addLot(e)
                }}
                onCancelClick={() => this.setState({ lotModalIsOpen: false })}
              />
              {this._renderLots()}
            </div>
          )}
        {this._renderPhotos()}
        {this._renderAttachments()}
        {!this.props.quickAdd && this._renderSigns()}
        {!this.props.quickAdd && this._renderComps()}
        {!this.props.quickAdd && this._renderTransactions()}
        {!this.props.quickAdd && this._renderRelatedConnections()}
        {!this.props.quickAdd && this._renderConnectChatChannel()}
        {!this.props.quickAdd && this._renderSocial()}
      </Container>
    )
  }

  private _renderApn() {
    return (
      <FormGroupRenderer
        labelText='APN / Parcel ID'
        name='ParcelID'
        bsSize='sm'
        value={this.props.property.parcelId}
        onChange={this.props.setParcelId}
      />
    )
  }

  private _renderAttachments() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <PageSection title='Attachments' collapse={true}>
        <FormGroupRenderer>
          <FileDropzone
            activeStyle={dropzoneActiveStyle}
            style={dropzoneStyle}
            identifier={ATTACHMENTS_IDENTIFIER}
          >
            <p
              style={{
                display: 'table-cell',
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              Drag and drop or click here to select files
            </p>
          </FileDropzone>
          <FileProgress
            dataSource={this.props.property.attachments}
            identifier={ATTACHMENTS_IDENTIFIER}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderBasicInfo(property: IProperty) {
    return (
      <PageSection title='Property Information'>
        <FormGroupRenderer>
          <Input
            placeholder='Property Name'
            value={property.name}
            onChange={(e) => this.props.setPropertyName(e.target.value)}
            required
            name='PropertyName'
            className='mb-2'
          />
          <AddressEditor
            onChange={this.props.setAddress}
            value={property.address}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderBuildingClass() {
    if (this.props.property.propertyTypes.contains(PropertyType.Office)) {
      return (
        <FormGroupRenderer labelText='Building Class'>
          <Input
            bsSize='sm'
            type='select'
            value={this.props.property.buildingClass}
            onChange={(e) => this.props.setBuildingClass(e.target.value)}
          >
            {this.props.configuration.buildingClasses
              .map((c, index) => (
                <option key={`buildingClasses-${index}`} value={c}>
                  {humanize(c)}
                </option>
              ))
              .toJS()}
          </Input>
        </FormGroupRenderer>
      )
    }

    return null
  }

  private _renderBuildingSize() {
    if (
      this.props.property.propertyTypes.contains(PropertyType.Land) ||
      this.props.property.propertyTypes.contains(
        PropertyType.AgriculturalLand,
      ) ||
      this.props.property.propertyTypes.contains(PropertyType.RecreationalLand)
    ) {
      return null
    }

    return (
      <FormGroupRenderer
        labelText='Building Size'
        bsSize='sm'
        type='number'
        step='0.01'
        name='BuildingSize'
        value={this.props.property.totalBuildingSize}
        onChange={this.props.setBuildingSize}
      />
    )
  }

  private _renderComps() {
    if (this.props.property.comps) {
      const compCards = this.props.property.comps.map((comp?: IComp) => {
        if (!comp) {
          return null
        }
        return (
          <div key={comp.propertyId} className='detail-card'>
            <CompCard
              comp={comp}
              actions={
                <div>
                  <DeleteConfirmationModal
                    isOpen={this.state.showConfirmDelete[comp.propertyId]}
                    itemName={comp.propertyName}
                    onConfirm={() => this.props.removeComp(comp)}
                    onToggleModal={() =>
                      this.setState({
                        showConfirmDelete: { [comp.propertyId]: false },
                      })
                    }
                  />
                  <Button
                    size='sm'
                    className='btn-outline-danger'
                    onClick={() =>
                      this.setState({
                        showConfirmDelete: { [comp.propertyId]: true },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={['fal', 'trash']} />
                  </Button>
                </div>
              }
            />
          </div>
        )
      })

      return (
        <div>
          <CompsEditor onChange={this.props.addComp} />
          <div className='detail-card-group mx-2'>{compCards}</div>
        </div>
      )
    }

    return null
  }

  private _renderCoordinates() {
    return (
      <PageSection title='Coordinates'>
        <FormGroupRenderer
          labelText='Latitude'
          bsSize='sm'
          name='RealEstateTaxes'
          type='number'
          step={0.00000001}
          value={this.props.property.gps.latitude}
          onChange={this.props.setLatitude}
        />
        <FormGroupRenderer
          labelText='Longitude'
          bsSize='sm'
          name='RealEstateTaxes'
          type='number'
          step={0.00000001}
          value={this.props.property.gps.longitude}
          onChange={this.props.setLongitude}
        />
      </PageSection>
    )
  }

  private _renderPropertyDescriptions() {
    return (
      <>
        <FormGroupRenderer labelText='Property Description'>
          <Input
            type='textarea'
            value={this.props.property.propertyDescription}
            style={{ height: '150px' }}
            onChange={(e) => this.props.setPropertyDescription(e.target.value)}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Location Description'>
          <Input
            type='textarea'
            value={this.props.property.locationDescription}
            style={{ height: '150px' }}
            onChange={(e) => this.props.setLocationDescription(e.target.value)}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Zoning Description'>
          <Input
            type='textarea'
            value={this.props.property.zoningDescription}
            style={{ height: '150px' }}
            onChange={(e) => this.props.setZoningDescription(e.target.value)}
          />
        </FormGroupRenderer>
      </>
    )
  }

  private _renderHealthCareFields() {
    if (this.props.property.propertyTypes.contains(PropertyType.HealthCare)) {
      return (
        <FormGroupRenderer
          labelText='No. of Beds'
          bsSize='sm'
          name='NoOfBeds'
          type='number'
          value={this.props.property.noOfUnits}
          onChange={this.props.setNumberOfUnits}
        />
      )
    }

    return null
  }

  private _renderHighlights() {
    return (
      <PageSection title='Highlights'>
        <StringListEditor
          addLabel='Add highlight'
          className='mx-3'
          onChange={this.props.setHighlights}
          value={this.props.property.highlights}
        />
      </PageSection>
    )
  }

  private _renderHotelMotelFields() {
    if (
      this.props.property.propertyTypes.contains(PropertyType.HotelAndMotel)
    ) {
      return (
        <div>
          <FormGroupRenderer labelText='Corridor'>
            <Input
              className='ml-2'
              bsSize='sm'
              type='select'
              value={this.props.property.corridor}
              onChange={(e) => this.props.setCorridor(e.target.value)}
            >
              {this.props.configuration.corridors
                .map((c, index) => (
                  <option key={`corridors-${index}`} value={c}>
                    {humanize(c)}
                  </option>
                ))
                .toJS()}
            </Input>
          </FormGroupRenderer>

          <FormGroupRenderer
            labelText='Average Daily Rate'
            bsSize='sm'
            name='AverageDailyRate'
            type='number'
            step='0.01'
            value={this.props.property.avgDailyRate}
            onChange={this.props.setAvgDailyRate}
          />

          <FormGroupRenderer
            labelText='Revenue Per PAR'
            bsSize='sm'
            name='RevenuePerPAR'
            type='number'
            step='0.01'
            value={this.props.property.revPerPAR}
            onChange={this.props.setRevPerPAR}
          />

          <FormGroupRenderer
            labelText='Average Occupancy'
            bsSize='sm'
            name='AverageOccupancy'
            type='number'
            step='0.01'
            value={this.props.property.avgOccupancy}
            onChange={this.props.setAvgOccupancy}
          />
        </div>
      )
    }
  }

  private _renderIndustrialFields() {
    if (this.props.property.propertyTypes.contains(PropertyType.Industrial)) {
      return (
        <div>
          <FormGroupRenderer
            labelText='Ceiling Height'
            bsSize='sm'
            type='number'
            value={this.props.property.ceilingHeight}
            onChange={this.props.setCeilingHeight}
          />

          <FormGroupRenderer
            labelText='Dock High Doors'
            bsSize='sm'
            type='number'
            value={this.props.property.noOfDockHighDoorsLoading}
            onChange={this.props.setDockHighDoors}
          />

          <FormGroupRenderer
            labelText='Drive In Doors'
            bsSize='sm'
            type='number'
            value={this.props.property.noOfDriveInGradeLevelDoors}
            onChange={this.props.setDriveInDoors}
          />
        </div>
      )
    }
  }

  private _renderLots() {
    if (this.props.property.lotDetails) {
      const lotCards = this.props.property.lotDetails.map((lot?: ILot) => {
        if (!lot) {
          return null
        }

        return (
          <div key={lot.id} className='detail-card'>
            <LotCard
              className='pt-2'
              lot={lot}
              editModalOpen={this.state.showConfirmEdit[lot.id]}
              onOkClicked={(e: ILot) => {
                this.setState({
                  showConfirmEdit: {
                    [e.id]: false,
                  },
                })

                this.props.editLot(e)
              }}
              onCancelClicked={() =>
                this.setState({ showConfirmEdit: { [lot.id]: false } })
              }
              actions={
                <div>
                  <DeleteConfirmationModal
                    isOpen={this.state.showConfirmDelete[lot.id]}
                    itemName={lot.lotNo}
                    onConfirm={() => this.props.removeLot(lot)}
                    onToggleModal={() =>
                      this.setState({ showConfirmDelete: { [lot.id]: false } })
                    }
                  />
                  <Button
                    size='sm'
                    color='secondary'
                    className='mr-2'
                    outline
                    onClick={() => this._toggleEditModal(lot.id, true)}
                  >
                    <FontAwesomeIcon icon={['fal', 'pencil']} />
                  </Button>
                  <Button
                    size='sm'
                    color='danger'
                    outline
                    onClick={() =>
                      this.setState({ showConfirmDelete: { [lot.id]: true } })
                    }
                  >
                    <FontAwesomeIcon icon={['fal', 'trash']} />
                  </Button>
                </div>
              }
            />
          </div>
        )
      })

      return (
        <PageSection title='Lots'>
          <Button
            className='ml-3'
            outline
            color='primary'
            size='sm'
            onClick={() => this.setState({ lotModalIsOpen: true })}
          >
            Add lot
          </Button>
          <div className='detail-card-group mx-2'>{lotCards}</div>
        </PageSection>
      )
    }

    return null
  }

  private _renderMultiFamilyFields() {
    if (this.props.property.propertyTypes.contains(PropertyType.Multifamily)) {
      return (
        <div>
          <FormGroupRenderer
            labelText='No. of Units'
            bsSize='sm'
            name='NoOfUnits'
            value={this.props.property.noOfUnits}
            onChange={this.props.setNumberOfUnits}
          />

          <FormGroupRenderer
            labelText='Gross Rent Multiplier'
            bsSize='sm'
            name='GrossRentMultiplier'
            type='number'
            step='0.01'
            value={this.props.property.grossRentMultiplier}
            onChange={this.props.setGrossRentMultiplier}
          />
        </div>
      )
    }

    return null
  }

  private _renderNumberOfRooms() {
    if (
      this.props.property.propertyTypes.contains(PropertyType.HotelAndMotel) ||
      this.props.property.propertyTypes.contains(PropertyType.SeniorHousing)
    ) {
      return (
        <FormGroupRenderer
          labelText='No. of Rooms'
          bsSize='sm'
          name='NoOfRooms'
          value={this.props.property.noOfUnits}
          onChange={this.props.setNumberOfUnits}
        />
      )
    }
  }

  private _renderOccupancy() {
    if (
      this.props.property.propertyTypes.contains(PropertyType.Land) ||
      this.props.property.propertyTypes.contains(PropertyType.HotelAndMotel) ||
      this.props.property.propertyTypes.contains(PropertyType.SeniorHousing)
    ) {
      return null
    }

    return (
      <FormGroupRenderer
        labelText='Occupancy'
        bsSize='sm'
        name='Occupancy'
        type='number'
        step='0.01'
        value={this.props.property.occupancy}
        onChange={this.props.setOccupancy}
      />
    )
  }

  private _renderOwners() {
    return (
      <PageSection title='Owners'>
        <FormGroupRenderer>
          <AssociatedContactsSelector
            name='Owners'
            onChange={this.props.setOwners}
            placeholder='Add owner(s)...'
            value={this.props.property.owners}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderPhotos() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <PageSection title='Photos' collapse={true}>
        <FormGroupRenderer>
          <FileDropzone
            activeStyle={dropzoneActiveStyle}
            style={dropzoneStyle}
            identifier={PHOTOS_IDENTIFIER}
          >
            <p
              style={{
                display: 'table-cell',
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              Drag and drop or click here to select files
            </p>
          </FileDropzone>
          <FileProgress
            dataSource={this.props.property.photos}
            identifier={PHOTOS_IDENTIFIER}
            mainPhotoId={this.props.property.thumbnailImageId}
            onSetMainPhoto={this.props.setMainPhoto}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderPropertyType() {
    return (
      <FormGroupRenderer labelText='Property Type(s)'>
        <PropertyTypeSelector
          configuration={this.props.configuration}
          value={this.props.property.propertyTypes}
          onChange={this.props.setPropertyTypes}
        />
      </FormGroupRenderer>
    )
  }

  private _renderPropertyInformation() {
    return (
      <PageSection title='Property Information'>
        {this._renderPropertyType()}
        {this._renderApn()}
        <FormGroupRenderer
          labelText='Real Estate Taxes'
          bsSize='sm'
          name='RealEstateTaxes'
          type='number'
          step='0.01'
          value={this.props.property.financialData.realEstateTaxes}
          onChange={this.props.setRealEstateTaxes}
        />
        <FormGroupRenderer
          labelText='Insurance Expenses'
          bsSize='sm'
          name='InsuranceExpenses'
          type='number'
          step='0.01'
          value={this.props.property.financialData.insuranceExpenses}
          onChange={this.props.setInsuranceExpenses}
        />
        <FormGroupRenderer
          labelText='Other Expenses'
          bsSize='sm'
          name='OtherExpenses'
          type='number'
          step='0.01'
          value={this.props.property.financialData.otherExpenses}
          onChange={this.props.setOtherExpenses}
        />
        {this._renderBuildingSize()}
        {this._renderBuildingClass()}
        <FormGroupRenderer labelText='Total Lot Size'>
          <LotSizeInput
            className='flex-grow-1 w-100'
            bsSize='sm'
            onChange={this.props.setLotSize}
            value={this.props.property.totalLotSize}
          />
        </FormGroupRenderer>
        {this._renderOccupancy()}
        <FormGroupRenderer
          labelText='Year Built'
          bsSize='sm'
          name='YearBuilt'
          type='number'
          value={this.props.property.yearBuilt}
          onChange={this.props.setYearBuilt}
        />
        {this._renderIndustrialFields()}
        {this._renderRetailFields()}
        {this._renderHealthCareFields()}
        {this._renderMultiFamilyFields()}
        {this._renderNumberOfRooms()}
        {this._renderHotelMotelFields()}
        {this._renderPropertyDescriptions()}
      </PageSection>
    )
  }

  private _renderRelatedConnections() {
    return (
      <PageSection title='Related Connections'>
        <FormGroupRenderer>
          <AssociatedContactsSelector
            value={this.props.property.associated}
            onChange={this.props.setRelatedConnections}
            placeholder='Add related connections...'
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderRetailFields() {
    if (this.props.property.propertyTypes.contains(PropertyType.Retail)) {
      return (
        <FormGroupRenderer
          labelText='Gross Leasable Area'
          bsSize='sm'
          name='GrossLeasableArea'
          type='number'
          step='0.01'
          value={this.props.property.grossLeaseableArea}
          onChange={this.props.setGrossLeasableArea}
        />
      )
    }

    return null
  }

  private _renderSigns() {
    return (
      <PageSection title='Signs'>
        <FormGroupRenderer>
          <SignListEditor
            addLabel='Add sign'
            value={this.props.property.signs}
            onChange={this.props.setSigns}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderConnectChatChannel() {
    return (
      <PageSection title='Chat Channel'>
        <FormGroupRenderer>
          <Input
            placeholder='Chat Channel ID'
            type='text'
            value={this.props.property.chatChannelId}
            onChange={(e) => this.props.setChatChannelId(e.target.value)}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderSocial() {
    return (
      <PageSection title='Social'>
        <FormGroupRenderer>
          {this.props.socialNetworks.map((s, i) => {
            if (s === undefined || i === undefined) {
              return null
            }

            const match =
              this.props.property.social.size > 0 &&
              this.props.property.social.find((item: any) => {
                return item.type === s.id
              })

            return (
              <SocialConnectionEditor
                className='d-flex flex-row align-items-center mt-2'
                userName={match ? match.username : ''}
                key={`social-${i}`}
                socialConnection={s}
                onChange={this.props.setSocial}
              />
            )
          })}
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderSpaces() {
    if (this.props.property.spaceDetails) {
      const spaceCards = this.props.property.spaceDetails.map(
        (space?: ISpace) => {
          if (!space) {
            return null
          }
          return (
            <div key={space.id} className='detail-card'>
              <SpaceCard
                className='mt-2'
                space={space}
                editModalOpen={this.state.showConfirmEdit[space.id]}
                onOkClicked={(e: ISpace) => {
                  this.setState({
                    showConfirmEdit: {
                      [e.id]: false,
                    },
                  })

                  this.props.editSpace(e)
                }}
                onCancelClicked={() =>
                  this.setState({ showConfirmEdit: { [space.id]: false } })
                }
                actions={
                  <div>
                    <DeleteConfirmationModal
                      isOpen={this.state.showConfirmDelete[space.id]}
                      itemName={space.spaceNo}
                      onConfirm={() => this.props.removeSpace(space)}
                      onToggleModal={() =>
                        this.setState({
                          showConfirmDelete: { [space.id]: false },
                        })
                      }
                    />
                    <Button
                      size='sm'
                      color='secondary'
                      className='mr-2'
                      onClick={() => this._toggleEditModal(space.id, true)}
                    >
                      <FontAwesomeIcon icon={['fal', 'pencil']} />
                    </Button>

                    <Button
                      size='sm'
                      className='btn-outline-danger'
                      onClick={() =>
                        this.setState({
                          showConfirmDelete: { [space.id]: true },
                        })
                      }
                    >
                      <FontAwesomeIcon icon={['fal', 'trash']} />
                    </Button>
                  </div>
                }
              />
            </div>
          )
        },
      )

      return (
        <PageSection title='Spaces'>
          <FormGroupRenderer>
            <Button
              className='ml-3'
              outline
              color='primary'
              size='sm'
              onClick={() => this.setState({ spaceModalIsOpen: true })}
            >
              Add space
            </Button>
            <div className='detail-card-group mx-2'>{spaceCards}</div>
          </FormGroupRenderer>
        </PageSection>
      )
    }

    return null
  }

  private _renderTransactions() {
    const transactions = this.props.property.transactionHistory.map((t, i) => {
      if (!t) {
        return null
      }

      return (
        <li key={`transaction-${t.id}`}>
          <div className='d-flex flex-row flex-wrap mt-2'>
            <div className='mr-2'>{formatDate(t.saleDate, 'MM/DD/YYYY')}</div>
            <div style={{ width: '100px' }}>{formatCurrency(t.salePrice)}</div>
            <PillListRenderer
              list={t.owners}
              baseNavigateUrl='/connection'
              idValue='contactId'
            />
            <DeleteConfirmationModal
              isOpen={this.state.showConfirmDelete[t.id]}
              itemName='this transaction'
              onConfirm={() => this.props.removeTransaction(t)}
              onToggleModal={() =>
                this.setState({ showConfirmDelete: { [t.id]: false } })
              }
            />
            <TransactionHistoryModal
              cancelModal={() =>
                this.setState({
                  showConfirmEdit: { [t.id]: false },
                  transactionModalIsOpen: false,
                })
              }
              confirmClick={(e: any) => this._toggleTransactionModal(e, 'edit')}
              isOpen={this.state.showConfirmEdit[t.id]}
              persistToServer={false}
              transaction={t}
              propertyId={this.props.property.id}
            />
            <div className='ml-auto'>
              <a
                className='text-primary'
                onClick={() =>
                  this.setState({
                    showConfirmEdit: { [t.id]: true },
                    transactionModalIsOpen: true,
                  })
                }
              >
                <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
              </a>
              <a
                className='text-danger'
                onClick={() =>
                  this.setState({
                    showConfirmDelete: { [t.id]: true },
                  })
                }
              >
                <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
              </a>
            </div>
          </div>
        </li>
      )
    })

    return (
      <PageSection title='Transactions'>
        <FormGroupRenderer>
          <TransactionHistoryModal
            cancelModal={() => this.setState({ transactionModalIsOpen: false })}
            confirmClick={(e: any) => this._toggleTransactionModal(e)}
            isOpen={this.state.transactionModalIsOpen}
            persistToServer={false}
            propertyId={this.props.property.id}
          />
          <Button
            outline
            color='primary'
            size='sm'
            onClick={() => this.setState({ transactionModalIsOpen: true })}
          >
            Add transaction
          </Button>
          <ul>{transactions}</ul>
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private async _saveProperty() {
    try {
      const gps = this.props.property.gps
      if (
        !this.props.match.params.id &&
        gps.latitude === 0 &&
        gps.longitude === 0
      ) {
        const loader = new Loader({
          apiKey: 'AIzaSyCWte3eHItNFTkYk0ZtpTxx_kO1Ph7kKj0',
          libraries: ['drawing', 'places'],
        })

        const google = await loader.load()

        const geocoder = new google.maps.Geocoder()
        const response = await geocoder.geocode({
          address:
            this.props.property.address.line1 +
            this.props.property.address.city +
            this.props.property.address.state,
        })

        if (response && response.results.length > 0) {
          this.props.setLatitude(response.results[0].geometry.location.lat())
          this.props.setLongitude(response.results[0].geometry.location.lng())
        }
      }
    } catch (err) {
      console.error(err)
    }

    this.props.save(this.props.property as Property)
  }

  private _toggleEditModal(id: string, value: boolean) {
    this.setState({ showConfirmEdit: { [id]: value } })
  }

  private _toggleTransactionModal(
    value: ITransactionHistory,
    mode: string = 'add',
  ) {
    this.setState({
      showConfirmEdit: { [value.id]: false },
      transactionModalIsOpen: false,
    })

    this.props.addTransaction(value, mode)
  }

  private _cancel() {
    this.props.history.goBack()
  }
}
